import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { LINK_TYPES } from '@siliconvault/shared';
import type { ItemLinkWithDetails } from '@siliconvault/shared';
import { useItemLinks, useCreateItemLink, useDeleteItemLink } from '../../hooks/useItemLinks';
import { useInventoryList } from '../../hooks/useInventory';
import { Button } from '../ui/Button';
import { Input, Select } from '../ui/Input';
import { Modal } from '../ui/Modal';
import { useToast } from '../ui/Toast';
import { useTranslation } from '../../i18n';

interface ItemLinksProps {
  itemId: string;
  canEdit: boolean;
}

export function ItemLinks({ itemId, canEdit }: ItemLinksProps) {
  const { data, isLoading } = useItemLinks(itemId);
  const createMutation = useCreateItemLink();
  const deleteMutation = useDeleteItemLink();
  const { toast } = useToast();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItemId, setSelectedItemId] = useState('');
  const [linkType, setLinkType] = useState<string>('peripheral');
  const [notes, setNotes] = useState('');

  const { data: searchResults } = useInventoryList(
    searchQuery.length >= 2 ? { search: searchQuery, limit: 5 } : { limit: 0 }
  );

  const links = data?.data ?? [];

  async function handleCreateLink() {
    if (!selectedItemId) return;
    try {
      await createMutation.mutateAsync({
        itemId,
        data: { peripheralItemId: selectedItemId, linkType, notes },
      });
      toast('Link created', 'success');
      setShowModal(false);
      setSearchQuery('');
      setSelectedItemId('');
      setNotes('');
    } catch {
      toast('Failed to create link', 'error');
    }
  }

  async function handleDeleteLink(linkId: string) {
    try {
      await deleteMutation.mutateAsync({ itemId, linkId });
      toast('Link removed', 'success');
    } catch {
      toast('Failed to remove link', 'error');
    }
  }

  return (
    <div style={{ marginTop: '1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
        <h3 style={{ fontSize: '0.8125rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-secondary)' }}>
          {t('links.title')}
        </h3>
        {canEdit && (
          <Button size="small" variant="secondary" onClick={() => setShowModal(true)}>
            + {t('links.plugIn').replace('+ ', '')}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div style={{ color: 'var(--text-muted)', fontSize: '0.8125rem' }}>{t('links.loading')}</div>
      ) : links.length === 0 ? (
        <div style={{
          padding: '1.25rem',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius)',
          color: 'var(--text-muted)',
          fontSize: '0.8125rem',
          fontFamily: 'var(--font-mono)',
          textAlign: 'center',
        }}>
          {t('links.noPeripherals')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {links.map((link) => (
            <LinkRow
              key={link.id}
              link={link}
              currentItemId={itemId}
              canEdit={canEdit}
              onDelete={() => handleDeleteLink(link.id)}
            />
          ))}
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={t('links.linkPeripheralTitle')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowModal(false)}>{t('links.cancel')}</Button>
            <Button onClick={handleCreateLink} disabled={!selectedItemId || createMutation.isPending}>
              {createMutation.isPending ? t('links.linking') : t('links.createLink')}
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <Input
            label={t('links.searchItems')}
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setSelectedItemId(''); }}
            placeholder={t('links.searchPlaceholder')}
          />
          {searchResults?.data && searchResults.data.length > 0 && (
            <div style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius)',
              maxHeight: '150px',
              overflowY: 'auto',
            }}>
              {searchResults.data
                .filter((item) => item.id !== itemId)
                .map((item) => (
                  <div
                    key={item.id}
                    onClick={() => { setSelectedItemId(item.id); setSearchQuery(item.name); }}
                    style={{
                      padding: '0.5rem 0.75rem',
                      cursor: 'pointer',
                      fontSize: '0.8125rem',
                      background: selectedItemId === item.id ? 'var(--accent-subtle)' : 'transparent',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    <strong>{item.name}</strong>
                    <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>{item.platform}</span>
                  </div>
                ))}
            </div>
          )}
          <Select label={t('links.linkType')} value={linkType} onChange={(e) => setLinkType(e.target.value)}>
            {LINK_TYPES.map((lt) => (
              <option key={lt} value={lt}>{lt}</option>
            ))}
          </Select>
          <Input
            label={t('links.notes')}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('links.optionalNotes')}
          />
        </div>
      </Modal>
    </div>
  );
}

function LinkRow({ link, currentItemId, canEdit, onDelete }: {
  link: ItemLinkWithDetails;
  currentItemId: string;
  canEdit: boolean;
  onDelete: () => void;
}) {
  const { t } = useTranslation();
  const isHost = link.hostItemId === currentItemId;
  const otherName = isHost ? link.peripheralItemName : link.hostItemName;
  const otherId = isHost ? link.peripheralItemId : link.hostItemId;
  const otherPlatform = isHost ? link.peripheralItemPlatform : link.hostItemPlatform;
  const direction = isHost ? '==>' : '<==';

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      padding: '0.625rem 0.875rem',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius)',
      fontFamily: 'var(--font-mono)',
      fontSize: '0.75rem',
    }}>
      <span style={{ color: 'var(--accent)', whiteSpace: 'nowrap' }}>
        [{link.linkType}]
      </span>
      <span style={{ color: 'cyan' }}>{direction}</span>
      <Link
        to="/inventory/$itemId"
        params={{ itemId: otherId }}
        search={{ from: undefined }}
        style={{ color: 'var(--text-primary)', textDecoration: 'none', flex: 1 }}
      >
        {otherName}
        <span style={{ color: 'var(--text-muted)', marginLeft: '0.5rem' }}>({otherPlatform})</span>
      </Link>
      {canEdit && (
        <Button size="small" variant="danger" onClick={onDelete}>
          {t('links.unplug')}
        </Button>
      )}
    </div>
  );
}
